/**
 * Helpers that keep the roster state in sync with the server using as few
 * full-state fetches as possible. The full state carries every retained
 * version of the roster, so a GET /state is the heaviest call the app makes.
 */
import type { HistorySnapshot, RosterState } from '@/types/api';

/**
 * Wrap an async task so overlapping calls share work instead of piling up.
 *
 * A call while nothing is running starts the task. Calls made while it runs all
 * share ONE follow-up run, started after the current one settles — so the last
 * caller still sees data fetched after its own request, but a burst of N edits
 * costs two fetches, not N.
 */
export function coalesceAsync(task: () => Promise<void>): () => Promise<void> {
  let running: Promise<void> | null = null;
  let queued: Promise<void> | null = null;
  const start = (): Promise<void> => {
    const current = task().finally(() => {
      if (running === current) running = null;
    });
    running = current;
    return current;
  };
  return () => {
    if (!running) return start();
    if (!queued) {
      queued = running
        .catch(() => undefined)
        .then(() => {
          queued = null;
          return start();
        });
    }
    return queued;
  };
}

/**
 * Fold this tab's own snapshot echo into the current state.
 *
 * The echo is the server's full post-edit state, sent over the WebSocket the
 * moment the edit lands — the same data a GET /state would return, already
 * delivered. Everything outside the grid (history, version pointer, officer
 * metadata and counts, notes, settings) is taken from it.
 *
 * The grid itself is not: the edit's own HTTP response patches it, and when the
 * user has already made a further optimistic edit, this echo predates it and
 * painting it would briefly undo that edit on screen.
 */
export function mergeOwnEcho(prev: RosterState | null, echo: RosterState): RosterState {
  if (!prev?.current_snapshot || !echo.generated) return echo;
  return { ...echo, current_snapshot: prev.current_snapshot };
}

/** From a broadcast snapshot: the newest of tab `clientId`'s writes it includes, or
 *  null when the server did not say (an older server, or no write from that tab yet). */
export function includedSeqFor(data: Record<string, unknown>, clientId: string): number | null {
  const seqs = data._seqs as Record<string, unknown> | undefined;
  const seq = seqs?.[clientId];
  return typeof seq === 'number' ? seq : null;
}

/** Strip the routing fields the server adds to a broadcast snapshot. */
export function stripEchoMeta(data: Record<string, unknown>): RosterState {
  const { _changed_by, _action, _client_id, _seqs, ...state } = data;
  return state as unknown as RosterState;
}

/**
 * `next`, with every part that is deep-equal to the same part of `prev` replaced by
 * `prev`'s own object.
 *
 * Every snapshot reaches the client as freshly parsed JSON, so even an edit that moved
 * one block handed the whole tree new identities — and every memo keyed on them (the
 * grid data, both canvases) rebuilt and repainted from scratch, three times per edit
 * (optimistic, response, echo). Shared, unchanged parts keep their identity, so only
 * what actually changed is rebuilt.
 */
export function shareEqual<T>(prev: T, next: T): T {
  if (Object.is(prev, next)) return prev;
  if (typeof prev !== 'object' || typeof next !== 'object' || prev === null || next === null) return next;
  if (Array.isArray(prev) !== Array.isArray(next)) return next;
  if (Array.isArray(prev)) {
    const a = prev as unknown[];
    const b = next as unknown as unknown[];
    let same = a.length === b.length;
    const out = b.map((v, i) => {
      const shared = i < a.length ? shareEqual(a[i], v) : v;
      if (shared !== a[i]) same = false;
      return shared;
    });
    return (same ? prev : out) as T;
  }
  const a = prev as Record<string, unknown>;
  const b = next as Record<string, unknown>;
  const keys = Object.keys(b);
  let same = keys.length === Object.keys(a).length;
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const shared = k in a ? shareEqual(a[k], b[k]) : b[k];
    if (shared !== a[k] || !(k in a)) same = false;
    out[k] = shared;
  }
  return (same ? prev : out) as T;
}

/**
 * The full state `next`, sharing what is unchanged with `prev` (see shareEqual).
 * The version list is headers only (see HistoryEntry), so it is cheap to compare; the
 * grid is compared against the grid already on screen, which is often an optimistic
 * one, so a confirmed edit does not redraw. O(size of the lean state): it no longer
 * depends on how many versions are kept.
 */
export function shareState(prev: RosterState | null, next: RosterState): RosterState {
  if (!prev || prev === next) return next;
  const nextCur = next.current_snapshot;
  const current_snapshot = nextCur && prev.current_snapshot ? shareEqual(prev.current_snapshot, nextCur) : nextCur;
  const { current_snapshot: _c, ...restNext } = next;
  const { current_snapshot: _pc, ...restPrev } = prev;
  const rest = shareEqual(restPrev as Record<string, unknown>, restNext as Record<string, unknown>);
  return { ...(rest as Omit<RosterState, 'current_snapshot'>), current_snapshot } as RosterState;
}

/** `snap` with its grid parts shared with `prev` where unchanged (see shareEqual). */
export function shareSnapshot(prev: HistorySnapshot | null | undefined, snap: HistorySnapshot): HistorySnapshot {
  return prev ? shareEqual(prev, snap) : snap;
}

/** How long another user's edit stays outlined on the grid after it arrives, ms. Each new edit
 *  restarts the clock for every trace still showing. */
export const EDIT_TRACE_TTL_MS = 15_000;

export interface GridTrace { row: number; col: number; color: string; traceType: 'add' | 'delete' | 'change' }

/**
 * The cells that differ between two grids, as edit traces: green added, red cleared,
 * `color` changed. Rows the patch left alone keep their identity and are skipped
 * without looking inside, so this costs the rows that changed, not the whole grid.
 */
export function gridTraces(
  before: string[][][] | undefined, after: string[][][] | undefined, color: string,
): GridTrace[] {
  if (!before || !after || before === after) return [];
  const traces: GridTrace[] = [];
  for (let row = 0; row < after.length; row++) {
    const was = before[row];
    const now = after[row];
    if (was === now) continue;
    for (let col = 0; col < (now?.length ?? 0); col++) {
      const oldOfficers = (was?.[col] ?? []).join(',');
      const newOfficers = (now[col] ?? []).join(',');
      if (oldOfficers === newOfficers) continue;
      const traceType = oldOfficers === '' ? 'add' : newOfficers === '' ? 'delete' : 'change';
      traces.push({ row, col, color: traceType === 'add' ? '#22c55e' : traceType === 'delete' ? '#dc2626' : color, traceType });
    }
  }
  return traces;
}
